import { Component, OnInit, effect, input, output, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { FormComponent, YesNoIconComponent } from '@rocksoft/cdk';
import { AppleWifiProfilePreset } from '@rocksoft/mdm-sdk-v2';
import { WifiProfile } from '../../../../viewmodels/apple/profiles/wifi-profile/wifi-profile.model';

/**
 * Component for displaying and editing WiFi profile configurations.
 */
@Component({
  selector: 'ml-mdm-wifi-profile',
  templateUrl: './wifi-profile.component.html',
  styleUrls: ['./wifi-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent, YesNoIconComponent, FormComponent, FormlyModule],
})
export class WifiProfileComponent implements OnInit {
  readonly wifiProfile = input.required<WifiProfile | AppleWifiProfilePreset>();
  readonly editingInput = input(false);
  readonly allowEditing = input(true);
  readonly showDisableAssociationMACRandomization = input(false);
  readonly showTitle = input(true);
  readonly showSubmitButton = input(false); // Only show submit button when adding a new profile

  readonly wifiProfileChanged = output<WifiProfile | AppleWifiProfilePreset>();

  readonly formComponent = viewChild(FormComponent);

  readonly editing = signal(false);

  fields: FormlyFieldConfig[] = [];

  encryptionTypeOptions = [
    { label: 'WEP', value: 'WEP' },
    { label: 'WPA / WPA2 Personal', value: 'WPA' },
    { label: 'WPA2 Personal (iOS 8 or later except Apple TV)', value: 'WPA2' },
    { label: 'Any (Personal)', value: 'Any' },
    { label: 'Dynamic WEP', value: 'WEP' },
    { label: 'WPA / WPA 2 Enterprise', value: 'WPA' },
    { label: 'WPA2 Enterprise (iOS 8 or later except Apple TV)', value: 'WPA2' },
    { label: 'Any (Enterprise)', value: 'Any' },
    { label: 'None', value: 'None' },
  ];

  proxyTypeOptions = [
    { label: 'None', value: 'None' },
    { label: 'Manual', value: 'Manual' },
    { label: 'Auto', value: 'Auto' },
  ];

  constructor() {
    effect(() => {
      this.editing.set(this.editingInput());
    });

    effect(() => {
      const wifiProfile = this.wifiProfile();
      this.updateFields(wifiProfile);
    });
  }

  ngOnInit(): void {
    this.updateFields(this.wifiProfile());
  }

  /**
   * Toggles between edit and view mode.
   * Only allows toggling if form is valid when exiting edit mode.
   */
  toggleEditing() {
    if (this.editing()) {
      const formComponent = this.formComponent();
      if (formComponent?.model) {
        this.onFormSubmit(formComponent.model);
      }
    }
    this.editing.set(!this.editing());
  }

  /**
   * Handles form submission by updating wifiProfile from form model and emitting wifiProfileChanged.
   * @param model - The form model data (keys match profile property names)
   */
  onFormSubmit(model: Record<string, unknown>): void {
    if (!this.wifiProfile()) {
      return;
    }

    // Check if form is valid before proceeding
    const formComponent = this.formComponent();
    if (formComponent?.form && !formComponent.form.valid) {
      return;
    }

    // Form model keys now match profile property names, so we can merge directly
    const updatedProfile = Object.assign({}, this.wifiProfile(), model);

    // // Update the input property (if using two-way binding)
    // this.wifiProfile = updatedProfile;

    // Emit the updated wifiProfile
    this.wifiProfileChanged.emit(updatedProfile);
  }

  /**
   * Type guard to check if a profile is an AppleWifiProfilePreset.
   * AppleWifiProfilePreset has uniqueId or id properties that WifiProfile doesn't have.
   * @param profile - The profile to check
   * @returns true if the profile is an AppleWifiProfilePreset, false otherwise
   */
  isAppleWifiProfilePreset(
    profile: WifiProfile | AppleWifiProfilePreset | null | undefined
  ): profile is AppleWifiProfilePreset {
    if (!profile) return false;
    return 'uniqueId' in profile || 'id' in profile;
  }

  /**
   * Gets the current proxy type value.
   * Returns the value from the form model when editing, or from the profile when viewing.
   * @returns The proxy type string ('None', 'Manual', or 'Auto')
   */
  get proxyType(): string {
    const formComponent = this.formComponent();
    if (this.editing() && formComponent?.model) {
      return (formComponent.model['proxyType'] as string) || '';
    }
    return this.wifiProfile()?.proxyType || '';
  }

  private updateFields(wifiProfile: WifiProfile | AppleWifiProfilePreset): void {
    const model = this.getFormModel();
    this.fields = [
      {
        key: 'SSID_STR',
        type: 'input-field',
        defaultValue: wifiProfile?.SSID_STR,
        className: 'field-separator-before',
        props: {
          label: 'Service Set Identifier (SSID)',
          required: true,
          description:
            'SSID of the Wi-Fi network to be used. In iOS 7.0 and later, this is optional if a DomainName value is provided.',
          labelPosition: 'top',
          attributes: { 'data-testid': 'wifi-ssid-input' },
        },
      },
      {
        key: 'HIDDEN_NETWORK',
        type: 'checkbox',
        defaultValue: wifiProfile?.HIDDEN_NETWORK,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Hidden network',
          description:
            'Besides SSID, the device uses information such as broadcast type and encryption type to differentiate a network. By default (false), it is assumed that all configured networks are open or broadcast. To specify a hidden network, must be true.',
          labelPosition: 'right',
        },
      },
      {
        key: 'autoJoin',
        type: 'checkbox',
        defaultValue: wifiProfile?.autoJoin,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Auto join',
          description:
            'Optional. Default true. If true, the network is auto-joined. If false, the user has to tap the network name to join it. Availability: Available in iOS 5.0 and later and in all versions of macOS.',
          labelPosition: 'right',
        },
      },
      {
        key: 'encryptionType',
        type: 'select',
        defaultValue: wifiProfile?.encryptionType,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Encryption type',
          description: 'The encryption type for the Wi-Fi network.',
          required: true,
          labelPosition: 'top',
          options: this.encryptionTypeOptions,
          attributes: { 'data-testid': 'wifi-encryption-type-select' },
        },
      },
      {
        key: 'proxyType',
        type: 'select',
        defaultValue: wifiProfile?.proxyType,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Proxy type',
          description: 'The proxy configuration type for the network.',
          required: true,
          labelPosition: 'top',
          options: this.proxyTypeOptions,
          attributes: { 'data-testid': 'wifi-proxy-type-select' },
        },
      },
      {
        key: 'proxyServer',
        type: 'input-field',
        defaultValue: wifiProfile?.proxyServer,
        hideExpression: (model) => model.proxyType !== 'Manual',
        props: {
          label: 'Proxy server',
          description: "The proxy server's network address.",
          labelPosition: 'top',
        },
      },
      {
        key: 'proxyServerPort',
        type: 'input-field',
        defaultValue: wifiProfile?.proxyServerPort,
        hideExpression: (model) => model.proxyType !== 'Manual',
        props: {
          label: 'Proxy server port',
          description: "The proxy server's port.",
          type: 'number',
          labelPosition: 'top',
        },
      },
      {
        key: 'proxyUsername',
        type: 'input-field',
        defaultValue: wifiProfile?.proxyUsername,
        hideExpression: (model) => model.proxyType !== 'Manual',
        props: {
          label: 'Proxy username',
          description: 'Optional. The username used to authenticate to the proxy server.',
          labelPosition: 'top',
        },
      },
      {
        key: 'proxyPassword',
        type: 'input-field',
        defaultValue: wifiProfile?.proxyPassword,
        hideExpression: (model) => model.proxyType !== 'Manual',
        props: {
          label: 'Proxy password',
          description: 'Optional. The password used to authenticate to the proxy server.',
          type: 'password',
          labelPosition: 'top',
        },
      },
      {
        key: 'proxyPACURL',
        type: 'input-field',
        defaultValue: wifiProfile?.proxyPACURL,
        hideExpression: (model) => model.proxyType !== 'Auto',
        props: {
          label: 'Proxy PAC Url',
          description: 'Optional. The URL of the PAC file that defines the proxy configuration.',
          labelPosition: 'top',
        },
      },
      {
        key: 'proxyPACFallbackAllowed',
        type: 'checkbox',
        defaultValue: wifiProfile?.proxyPACFallbackAllowed,
        hideExpression: (model) => model.proxyType !== 'Auto',
        wrappers: [],
        props: {
          label: 'Proxy Allow PAC fallback',
          description:
            'Optional. If false, prevents the device from connecting directly to the destination if the PAC file is unreachable. Default is false. Availability: Available in iOS 7 and later.',
          labelPosition: 'right',
        },
      },
      {
        key: 'captiveBypass',
        type: 'checkbox',
        defaultValue: model.captiveBypass,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Captive bypass',
          description:
            'Optional. If set to true, Captive Network detection will be bypassed when the device connects to the network. Defaults to false.',
          labelPosition: 'right',
        },
      },
      {
        key: 'disableAssociationMACRandomization',
        type: 'checkbox',
        defaultValue: model.disableAssociationMACRandomization,
        wrappers: [],
        className: 'field-separator-before',
        props: {
          label: 'Disable Association MAC Randomization',
          description:
            'If true, disables MAC address randomization for a Wi-Fi network while associated with that network. This feature also shows a privacy warning in Settings indicating that the network has reduced privacy protections. If false, then the system enables MAC address randomization. This value is only locked when MDM installs the profile. If the profile is manually installed, the system sets the value but the user can change it. Available in iOS 14 and later, macOS 15 and later, and watchOS 7 and later.',
          labelPosition: 'right',
        },
      },
      {
        key: 'password',
        type: 'input-field',
        defaultValue: model.password,
        className: 'field-separator-before',
        props: {
          label: 'Password',
          description: 'The Wi-Fi password.',
          type: 'password',
          showPasswordToggle: true,
          labelPosition: 'top',
        },
      },
    ];

    // Update the form model if form component is available
    const formComponent = this.formComponent();
    if (formComponent) {
      formComponent.model = model;
    }
  }

  private getFormModel(): Record<string, unknown> {
    const wifiProfile = this.wifiProfile();
    return {
      SSID_STR: wifiProfile?.SSID_STR || '',
      HIDDEN_NETWORK: wifiProfile?.HIDDEN_NETWORK || false,
      autoJoin: wifiProfile?.autoJoin || false,
      encryptionType: wifiProfile?.encryptionType || '',
      proxyType: wifiProfile?.proxyType || '',
      proxyServer: wifiProfile?.proxyServer || '',
      proxyServerPort: wifiProfile?.proxyServerPort || 0,
      proxyUsername: wifiProfile?.proxyUsername || '',
      proxyPassword: wifiProfile?.proxyPassword || '',
      proxyPACURL: wifiProfile?.proxyPACURL || '',
      proxyPACFallbackAllowed: wifiProfile?.proxyPACFallbackAllowed || false,
      captiveBypass: wifiProfile?.captiveBypass || false,
      disableAssociationMACRandomization: wifiProfile?.disableAssociationMACRandomization || false,
      password: wifiProfile?.password || '',
    };
  }
}
